import React from 'react';
import { Tooltip, Box, Typography, Button, Chip } from '@mui/material';
import { RateReview as AuditIcon, School as SubjectIcon } from '@mui/icons-material';
import { DataManagementConfig, TableColumn } from '@shared/components/data-management/types';
import { UserNameDisplay, SubjectNameDisplay } from '@shared/components';
import { SurveyReportTableData, SurveyReportFormData } from '../types';
import { useAllTenantReportsDataManagement } from '../hooks/useAllTenantReportsForDataManagement';

export const useAllTenantReportsConfig = (
  tenantId: string,
  navigate?: (path: string) => void
): DataManagementConfig<SurveyReportTableData, SurveyReportFormData, SurveyReportFormData> => {
  const { useAllTenantReportsQuery, useDeleteSurveyReportWrapper } =
    useAllTenantReportsDataManagement(tenantId);

  // View Report Action (only for generated reports)
  const viewReportAction = {
    label: 'View Report',
    onClick: (row: SurveyReportTableData) => {
      if (navigate) {
        navigate(`/app/manage/universities/${tenantId}/reports/${row.id}/view`);
      }
    },
    color: 'primary' as const,
    variant: 'contained' as const,
    condition: (row: SurveyReportTableData) => !!row.generatedAt, // Only show for generated reports
  };

  return {
    // Operations (hooks)
    hooks: {
      useQuery: useAllTenantReportsQuery,
      useCreate: undefined, // No creation from this view
      useUpdate: undefined, // No updates from this view
      useDelete: useDeleteSurveyReportWrapper,
    },

    table: {
      title: 'All Tenant Survey Reports',
      columns: [
        {
          label: 'Status',
          key: 'generatedAt',
          render: (value: string | undefined) => {
            if (value) {
              return <Chip label="Published" color="success" size="small" />;
            }

            return (
              <Tooltip title="This report is waiting for the audit to be completed.">
                <Chip label="Pending Audit" color="warning" size="small" />
              </Tooltip>
            );
          },
        },
        {
          label: 'Subject',
          key: 'subjectId',
          render: (value: string | undefined, row: SurveyReportTableData) => {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SubjectIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {value ? (
                      <SubjectNameDisplay
                        tenantId={tenantId}
                        subjectId={value}
                        fallback={row.subjectName || 'Unknown Subject'}
                      />
                    ) : (
                      row.subjectName || 'Unknown Subject'
                    )}
                  </Typography>
                  {row.instituteName && (
                    <Typography variant="caption" color="text.secondary">
                      {row.instituteName}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          },
        },
        {
          label: 'Survey',
          key: 'surveyTitle',
          render: (value: string | undefined) => value || 'Unknown Survey',
        },
        {
          label: 'Responses',
          key: 'includedResponses',
          render: (value: number) => {
            return `${value || 0}`;
          },
        },
        {
          label: 'Created By',
          key: 'generatedBy',
          render: (value: string | undefined, row: SurveyReportTableData) => {
            const userId = value || row.createdBy;
            return (
              <Typography variant="body2" color="text.secondary">
                {userId ? <UserNameDisplay userId={userId} fallback="System" /> : 'System'}
              </Typography>
            );
          },
        },
        {
          label: 'Action',
          key: 'auditId',
          render: (auditId: string, row: SurveyReportTableData) => {
            // Only show "Go to Audit" for pending reports (no generatedAt)
            if (!row.generatedAt) {
              const actualAuditId = row.auditId || auditId;

              if (!actualAuditId) {
                return (
                  <Tooltip title="Audit ID is missing. Cannot navigate to audit.">
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<AuditIcon />}
                        disabled
                        sx={{ minWidth: 'auto' }}
                      >
                        Go to Audit
                      </Button>
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<AuditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (navigate) {
                      navigate(`/app/survey-audits/${actualAuditId}`);
                    }
                  }}
                  sx={{ minWidth: 'auto' }}
                >
                  Go to Audit
                </Button>
              );
            }
            return null;
          },
        },
        {
          label: 'Date',
          key: 'generatedAt',
          render: (value: string | undefined) => {
            return value ? new Date(value).toLocaleDateString() : '';
          },
        },
      ] as TableColumn<SurveyReportTableData>[],
    },

    // Actions for each row
    actions: {
      custom: [viewReportAction],
    },

    // Features configuration
    features: {
      search: true,
      pagination: true,
      onRowClick: (row: SurveyReportTableData) => {
        // Only allow row click navigation for generated reports
        if (navigate && row.generatedAt) {
          navigate(`/app/manage/universities/${tenantId}/reports/${row.id}/view`);
        }
      },
    },
  };
};
